// packages
import { createContext, useContext, useEffect, useMemo, useState, useCallback, ReactNode } from 'react'
import { InboundMessage, Realtime, RealtimeChannel } from 'ably'
import { format } from 'date-fns-tz'
import { subHours } from 'date-fns'

// entities
import { user_role } from '@/entities/user'

// hooks
import { useToast } from '@/hooks/use-toast'

// utils
import { timeZone } from '@/utils/dates-util'

// store
import { useAuthStore } from '@/store/auth'

// constants
import { WS_EXAM_CONSULTANT_EVENTS, WS_CHECKOUT_EVENTS, WS_SIGNS_SYMPTOMS_EVENTS, WS_EXAM_ANALYSIS_EVENTS } from '@/constants/ws'

// stores (para refetch após eventos)
import { useSignsSymptomsStore } from '@/store/signs-symptoms'
import { useExamAnalysisStore } from '@/store/exam-analysis'

// lib
import { showError, showInfo, showSuccess, showWSMessage } from '@/lib/alerts'

// interfaces
interface IWebSocketContextProps {
  ws: Realtime | null
  wsChannels: RealtimeChannel[]
  queue: SetChannelProps[]
  setChannel: ({ channelName }: SetChannelProps) => void
  // disconnect: () => Promise<void>
}

// types
import { WSChannelMessageTypeProps, WSExamConsultantEventProps, WSCheckoutEventProps, WSSignsSymptomsEventProps, WSExamAnalysisEventProps } from '@/types/ws-types'
type SetChannelProps = {
  channelName: string
  forceReconnect?: boolean
}

// contexts
const WebSocketContext = createContext<IWebSocketContextProps | undefined>(undefined)

// variables
const loc = `@/contexts/WebSocketContext`

export const WebSocketProvider = ({ children }: { children: Readonly<ReactNode> }) => {
  const { toast } = useToast()
  const { user, updateBalance } = useAuthStore()
  const [ws, setWs] = useState<Realtime | null>(null)
  const [wsChannels, setWsChannels] = useState<RealtimeChannel[]>([])
  const [queue, setQueue] = useState<SetChannelProps[]>([])
  const showLogs = (import.meta.env.VITE_NODE_ENV != 'production' || user?.role === user_role.ADMIN) && false

  useEffect(() => {
    if (ws?.connection?.state === 'connected') return

    const ably = new Realtime({ key: import.meta.env.VITE_WS_API_KEY })
    let isPageReloading = false

    const handleBeforeUnload = () => {
      isPageReloading = true
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    ably.connection.on('connected', () => {
      if (showLogs) console.log(`Connected to WebSocket.`)
      setWs(ably)
    })

    ably.connection.on('failed', () => {
      if (showLogs) console.error(`Unhandled error at ${loc}.ably.connection.onFailed. Details: ${JSON.stringify(ably.connection.errorReason, null, 2)}`)

      // TODO: descomentar
      // if (ably.connection.errorReason?.stack && import.meta.env.VITE_NODE_ENV === 'development') toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível se conectar ao servidor de WebSocket.' })
    })

    ably.connection.on('disconnected', () => {
      if (showLogs) console.error(`Unhandled error at ${loc}.ably.connection.onDisconnected. Details: ${JSON.stringify(ably.connection.errorReason, null, 2)}`)

      // TODO: descomentar
      // if (ably.connection.errorReason?.stack && import.meta.env.VITE_NODE_ENV === 'development') toast({ variant: 'destructive', title: 'Ops ...', description: 'A conexão com o servidor de WebSocket foi perdida.' })
    })

    ably.connection.on('suspended', () => {
      if (showLogs) console.error(`Unhandled error at ${loc}.ably.connection.onSuspended. Details: ${JSON.stringify(ably.connection.errorReason, null, 2)}`)

      // TODO: descomentar
      // if (ably.connection.errorReason?.stack && import.meta.env.VITE_NODE_ENV === 'development') toast({ variant: 'destructive', title: 'Ops ...', description: 'A conexão com o servidor de WebSocket foi suspensa.' })
    })

    ably.connection.on('closed', () => {
      if (!isPageReloading) {
        if (showLogs) console.error(`Unhandled error at ${loc}.ably.connection.onClosed. Details: ${JSON.stringify(ably.connection.errorReason, null, 2)}`)

        // TODO: descomentar
        // if (ably.connection.errorReason?.stack && import.meta.env.VITE_NODE_ENV === 'development') toast({ variant: 'destructive', title: 'Ops ...', description: 'A conexão com o servidor de WebSocket foi fechada.' })
      }
    })

    return () => {
      if (showLogs) console.log(`Closing WebSocket connection at ${loc}.useEffect function.`)

      window.removeEventListener('beforeunload', handleBeforeUnload)

      ably.connection.off()
      ably.connection.close()
      ably.close()
    }
  }, [])

  const setChannel = useCallback(
    async ({ channelName, forceReconnect }: SetChannelProps) => {
      if (ws?.connection?.state != 'connected') {
        const alreadyAddedToQueue = queue.some(q => q.channelName === channelName)

        if (!alreadyAddedToQueue) {
          setQueue(prev => [...prev, { channelName }])
          if (showLogs) console.log(`WebSocket instance not connected yet, adding to queue...\nQUEUE: ${queue} \nCHANNEL NAME: ${channelName}`)
        } else {
          if (showLogs) console.log(`WebSocket instance not connected yet, but ${channelName} is already added to the queue...`)
        }

        return
      }

      if (!forceReconnect) {
        const ch = wsChannels.find(wsCh => wsCh?.name === channelName)

        if (!['initialized', 'attached', undefined].includes(ch?.state)) {
          if (showLogs) console.log(`WebSocket channel has ${ch?.state} status.`)
          return
        }
      }

      const channel = ws.channels.get(channelName)

      channel.on('attached', () => {
        const time = format(subHours(new Date(), 3), 'dd/MM/yyyy HH:mm:ss', { timeZone })
        if (showLogs) console.log(`Connected to WebSocket Channel ${channelName} at ${time}.`)
        setWsChannels(prev => [...prev, channel])
      })

      channel.on('failed', () => {
        if (showLogs) console.error(`Unhandled error at ${loc}.channel.onFailed. Details: ${JSON.stringify(channel.errorReason, null, 2)}`)

        if (channel.errorReason?.stack) {
          // TODO: descomentar
          // if (import.meta.env.VITE_NODE_ENV === 'development') toast({ variant: 'destructive', title: 'Ops ...', description: `Não foi possível se conectar ao canal ${channelName} do servidor de WebSocket.` })
          _channelCb(channel.name, 'ERROR', channel.errorReason.stack)
        }
      })

      channel.on('suspended', () => {
        if (showLogs) console.error(`Unhandled error at ${loc}.channel.onSuspended. Details: ${JSON.stringify(channel.errorReason, null, 2)}`)

        if (channel.errorReason?.stack) {
          // TODO: descomentar
          // if (import.meta.env.VITE_NODE_ENV === 'development') toast({ variant: 'destructive', title: 'Ops ...', description: `A conexão com o canal ${channelName} do servidor de WebSocket foi suspensa.` })
          _channelCb(channel.name, 'ERROR', channel.errorReason.stack)
        }
      })

      channel.on('detached', () => {
        if (showLogs) console.error(`Unhandled error at ${loc}.channel.onDetached. Details: ${JSON.stringify(channel.errorReason, null, 2)}`)

        if (channel.errorReason?.stack) {
          // TODO: descomentar
          // if (import.meta.env.VITE_NODE_ENV === 'development') toast({ variant: 'destructive', title: 'Ops ...', description: `A conexão com o canal ${channelName} do servidor de WebSocket foi desanexada.` })
          _channelCb(channel.name, 'ERROR', channel.errorReason.stack)
        }
      })

      await channel.subscribe('message', (msg: InboundMessage) => _channelCb(channel.name, 'MESSAGE', msg.data))

      return async () => {
        if (showLogs) console.log(`Closing WebSocket channel connection at ${loc}.setChannel function.`)

        channel.unsubscribe('message')
        channel.off()
        await channel.detach()
        ws.channels.release(channelName)
      }
    },
    [ws, wsChannels, queue]
  )

  const _channelCb = async (channelName: string, type: WSChannelMessageTypeProps, msg: string) => {
    try {
      if (type === 'ERROR') {
        console.error(`Unhandled error received on WebSocket channel callback. Details: ${JSON.stringify({ channelName, type, msg })}`)
        return
      } else if (type === 'MESSAGE') {
        const parsedMsg: WSExamConsultantEventProps | WSCheckoutEventProps | WSSignsSymptomsEventProps | WSExamAnalysisEventProps = typeof msg === 'string' ? JSON.parse(msg || '{}') : msg
        const data: { title: string; description: string } = JSON.parse(parsedMsg?.data || '{ title: "Mensagem recebida", description: "Deseja visualizar?" }')
        let isConfirmed: boolean

        switch (parsedMsg?.action) {
          case WS_EXAM_CONSULTANT_EVENTS.EXAM_CONSULTANT_DONE:
            void ({ isConfirmed } = await showWSMessage(data.title, data.description))
            if (isConfirmed) window.location.href = `/exam-consultant?ref=${(parsedMsg as WSExamConsultantEventProps).ref}`
            break

          case WS_EXAM_CONSULTANT_EVENTS.EXAM_CONSULTANT_FAIL:
            showError(data.description)
            break

          case WS_CHECKOUT_EVENTS.CHECKOUT_PAID:
            showSuccess(data.description)
            updateBalance((parsedMsg as WSCheckoutEventProps).newBalance)
            break

          case WS_CHECKOUT_EVENTS.CHECKOUT_FAILED:
            showError(data.description)
            break

          case WS_CHECKOUT_EVENTS.CHECKOUT_REFUNDED:
            showInfo(data.description)
            break

          case WS_SIGNS_SYMPTOMS_EVENTS.SIGNS_SYMPTOMS_ANALYSIS_DONE: {
            // Atualiza o cache local da listagem
            const ref = (parsedMsg as WSSignsSymptomsEventProps).ref
            await useSignsSymptomsStore.getState().fetchByRef(ref)
            const { isConfirmed: ok } = await showWSMessage(data.title, data.description)
            if (ok) window.location.href = `/signs-symptoms?ref=${ref}`
            break
          }

          case WS_SIGNS_SYMPTOMS_EVENTS.SIGNS_SYMPTOMS_ANALYSIS_FAIL: {
            const ref = (parsedMsg as WSSignsSymptomsEventProps).ref
            await useSignsSymptomsStore.getState().fetchByRef(ref)
            showError(data.description)
            break
          }

          case WS_EXAM_ANALYSIS_EVENTS.EXAM_ANALYSIS_DONE: {
            const ref = (parsedMsg as WSExamAnalysisEventProps).ref
            await useExamAnalysisStore.getState().fetchByRef(ref)
            const { isConfirmed: ok } = await showWSMessage(data.title, data.description)
            if (ok) window.location.href = `/exam-analysis?ref=${ref}`
            break
          }

          case WS_EXAM_ANALYSIS_EVENTS.EXAM_ANALYSIS_FAIL: {
            const ref = (parsedMsg as WSExamAnalysisEventProps).ref
            await useExamAnalysisStore.getState().fetchByRef(ref)
            showError(data.description)
            break
          }

          default:
            console.error(`Msg action not recognized: ${JSON.stringify(parsedMsg, null, 2)}`)
            break
        }
      } else {
        if (import.meta.env.VITE_NODE_ENV === 'development') toast({ variant: 'destructive', title: 'Ops ...', description: 'Tipo de evento não reconhecido.' })
      }
    } catch (err) {
      console.error(`Unhandled rejection at ${loc}._channelCb function. Details: ${err}`)
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível receber o evento.' })
    }
  }

  useEffect(() => {
    if (queue.length && ws?.connection?.state === 'connected') {
      for (const item of queue) {
        if (showLogs) console.log(`LOOPING DA QUEUE`) // TODO: remover

        const ch = wsChannels.find(wsCh => wsCh?.name === item.channelName)

        if (ch?.name != item.channelName) {
          if (showLogs) console.log(`EXECUTANDO A QUEUE ... \nCHANNEL STATE: ${ch?.state} \nCHANNEL NAME: ${ch?.name} \nQUEUE ITEM CHANNEL NAME: ${item.channelName}`) // TODO: remover
          setChannel(item)
        } else {
          if (showLogs) console.log(`QUEUE NÃO ENTROU. \nCHANNEL STATE: ${ch?.state} \nCHANNEL NAME: ${ch?.name} \nQUEUE ITEM CHANNEL NAME: ${item.channelName}`)
        }
      }
    }
  }, [queue, ws?.connection?.state, setChannel])

  /* const disconnect = useCallback(async () => {
    try {
      if (wsChannel) {
        wsChannel.unsubscribe('message')
        wsChannel.off()

        await new Promise<void>(resolve => {
          if (wsChannel.state === 'attached') {
            wsChannel.on('detached', () => {
              ws?.channels?.release(wsChannel.name)
              resolve()
            })

            wsChannel.detach()
          } else ws?.channels?.release(wsChannel.name)
        })

        setWsChannel(null)
      }

      setQueue([])

      if (showLogs) console.log(`Disconnected from WebSocket and channel at ${loc}.disconnect function.`)
    } catch (err) {
      if (showLogs) console.error(`Error disconnecting WebSocket at ${loc}.disconnect function. Details: ${err}`)
    }
  }, [ws, wsChannel, showLogs]) */

  // useEffect(() => {
  //   return () => {
  //     disconnect()
  //   }
  // }, [disconnect])

  const value = useMemo(
    () => ({
      ws,
      wsChannels,
      queue,
      setChannel
      // disconnect
    }),
    [ws, wsChannels, queue, setChannel /* , disconnect */]
  )

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>
}

export const useWebSocket = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within an WebSocketProvider')
  }
  return context
}
